import type { ReactNode } from "react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { BackButton } from "@/components/common/BackButton";
import { AddForm } from "@/components/design/AddForm";
import { AddPlaceholder } from "@/components/design/AddPlaceholder";
import { Button } from "@/components/design/Button";
import { Disclosure } from "@/components/design/Disclosure";
import { FilterChip } from "@/components/design/FilterChip";
import { MenuItem } from "@/components/design/MenuItem";
import { NumberStepper } from "@/components/design/NumberStepper";
import { PlusButton } from "@/components/design/PlusButton";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Switch } from "@/components/design/Switch";

// Styleguide · Buttons sub-tab. Focused deep-dive into action surfaces;
// the Übersicht tab is the broader catalogue. Future sibling sub-tabs
// could be Text / Farben / Effekte — named by topic.
//
// Same shape as the spark-buttons audit HTML:
//   1. Canonical <Button> variants (+ BackButton)
//   2. Sibling primitives in components/design/
//   3. Audit notes for the button-alignment pass.
// Specimens render real components so the visuals stay accurate as primitives
// evolve. Counts and callsite references are a snapshot — see the date stamp.

const SNAPSHOT_DATE = "2026-06-14";

interface Example {
  file: string;
  label: string;
}

interface RowSpec {
  num: string;
  name: string;
  count: string;
  code: string;
  purpose: string;
  examples: Example[];
  notes?: string;
  specimens: ReactNode;
}

function Row({ num, name, count, code, purpose, examples, notes, specimens }: RowSpec) {
  return (
    <div className="flex flex-col gap-2 border-t border-ink/5 pt-4 first:border-t-0 first:pt-0">
      <div className="flex items-baseline gap-2 text-[11px] text-ink/70">
        <span className="font-mono text-[10px] text-ink/40">{num}</span>
        <span className="font-medium uppercase tracking-wider">{name}</span>
        <span className="rounded-full bg-ink/[0.06] px-2 py-0.5 font-mono text-[9px] text-ink/55">
          {count}
        </span>
      </div>
      <div className="flex min-h-[44px] flex-wrap items-center gap-3">{specimens}</div>
      <code className="font-mono text-[10px] text-ink/45">{code}</code>
      <p className="text-[12px] leading-relaxed text-ink">
        <span className="font-medium">
          <FormattedMessage id="settings.styleguide.buttons.purposeLabel" defaultMessage="Purpose:" />
        </span>{" "}
        {purpose}
      </p>
      <div className="text-[11px] text-ink/70">
        <span className="font-medium">
          <FormattedMessage id="settings.styleguide.buttons.examplesLabel" defaultMessage="Examples in code:" />
        </span>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          {examples.map((e, i) => (
            <li key={i}>
              <code className="font-mono text-[10px] text-ink/60">{e.file}</code> — {e.label}
            </li>
          ))}
        </ul>
      </div>
      {notes ? <p className="text-[10px] italic text-ink/50">{notes}</p> : null}
    </div>
  );
}

function Section({ title, sub, children, success }: { title: string; sub?: string; children?: ReactNode; success?: boolean }) {
  return (
    <div
      className={
        success
          ? "box bg-emerald-50 p-6 ring-1 ring-emerald-200"
          : "box bg-surface p-6"
      }
    >
      <div
        className={
          success
            ? "text-[10px] uppercase tracking-[0.25em] text-emerald-700"
            : "text-[10px] uppercase tracking-[0.25em] text-ink/55"
        }
      >
        {title}
      </div>
      {sub ? (
        <div className={success ? "mt-1 text-[12px] text-emerald-800" : "mt-1 text-[12px] text-ink/55"}>{sub}</div>
      ) : null}
      {children ? <div className="mt-4 flex flex-col">{children}</div> : null}
    </div>
  );
}

export function ButtonsTab() {
  const intl = useIntl();
  // Interactive state for the few specimens that have it. Mostly cosmetic in
  // this tab — the live source of truth for behavior is the Primitives tab.
  const [chipState, setChipState] = useState(false);
  const [switchOn, setSwitchOn] = useState(true);
  const [switchOn2, setSwitchOn2] = useState(true);
  const [stepperValue, setStepperValue] = useState(8);
  const [seg, setSeg] = useState<"smart" | "self">("smart");
  const [filter, setFilter] = useState<"alle" | "projekte" | "posts">("alle");

  return (
    <>
      {/* Intro card */}
      <div className="box bg-surface p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-ink/55">
          <FormattedMessage id="settings.styleguide.buttons.introEyebrow" defaultMessage="UI · Buttons in detail" />
        </div>
        <p className="mt-3 max-w-3xl text-[12px] leading-relaxed text-ink/70">
          <FormattedMessage
            id="settings.styleguide.buttons.introBody"
            defaultMessage="Every button and action primitive of the app with concrete production callsites. Specimens are real components – what you see here is exactly what the app renders."
          />
        </p>
        <p className="mt-2 max-w-3xl text-[11px] leading-relaxed text-ink/55">
          <span className="font-medium text-ink/70">
            <FormattedMessage
              id="settings.styleguide.buttons.snapshotNoticeLead"
              defaultMessage="Snapshot, not authoritative."
            />
          </span>{" "}
          <FormattedMessage
            id="settings.styleguide.buttons.snapshotNotice"
            defaultMessage="Counts and examples below come from a grep run on {date}. Reliable rules (recipes, anti-patterns) live in {doc}; check there before code changes – this is only a representative overview."
            values={{
              date: <span className="font-mono">{SNAPSHOT_DATE}</span>,
              doc: <code className="font-mono text-[10px]">docs/UI_CONVENTIONS.md</code>, // i18n-allow
            }}
          />
        </p>
      </div>

      <Section
        title={intl.formatMessage({
          id: "settings.styleguide.buttons.section1Title",
          defaultMessage: "1 – Canonical '<Button>' variants",
        })}
        sub={intl.formatMessage({
          id: "settings.styleguide.buttons.section1Sub",
          defaultMessage:
            "Seven variants in components/design/Button.tsx via button-classes.ts. Plus the BackButton navigation primitive.",
        })}
      >
        <Row
          num="1"
          name="Primary"
          count="44 uses"
          code='<Button variant="primary">'
          purpose="The affirmative side of any choice — Submit / Confirm / Create / Add / Continue. Default when no variant is passed."
          examples={[
            { file: "components/layout/SaveBar.tsx:57", label: "Persistent save action in the unsaved-changes toast" },
            { file: "components/match/MatchWorkflow.tsx:224", label: "Next-step button in the 4-step Match workflow" },
            { file: "components/layout/Hero.tsx:52", label: "CTA shared across all four pillar landings" },
            { file: "pages/admin/QuestionnaireAdminPage.tsx:106", label: "+ New questionnaire in the admin sidebar" },
          ]}
          notes="Distributed across Save, Match, Share, Grow, and Admin — no domain coupling."
          specimens={
            <>
              <Button>
                <FormattedMessage id="common.save" defaultMessage="Save" />
              </Button>
              <Button>
                + <FormattedMessage id="common.add" defaultMessage="Add" />
              </Button>
              <Button disabled>
                <FormattedMessage id="common.create" defaultMessage="Create" />
              </Button>
            </>
          }
        />

        <Row
          num="2"
          name="Secondary"
          count="55 uses"
          code='<Button variant="secondary">'
          purpose="The inverted-ghost — quiet at rest (transparent, 70% ink text, 15% ink ring) so the adjacent primary leads the eye. On hover it commits decisively, flipping to ink-on-page. Used for dismiss / cancel and for low-emphasis CTAs that still feel deliberate on click."
          examples={[
            { file: "components/common/ConfirmDialog.tsx:35", label: "Cancel action in the confirmation dialog" },
            { file: "components/design/EditPanelButtons.tsx:70", label: "Cancel in the shared edit-panel footer" },
            { file: "components/common/LoadMore.tsx:55", label: "Mehr laden in paginated lists" },
            { file: "pages/share/SharePage.tsx:254", label: "↗ Share trigger on Share landing cards" },
          ]}
          specimens={
            <>
              <Button variant="secondary">
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              <Button variant="secondary">
                ↗ <FormattedMessage id="settings.styleguide.buttons.specimen.publish" defaultMessage="Publish" />
              </Button>
              <Button variant="secondary">
                ↘ <FormattedMessage id="settings.styleguide.buttons.specimen.link" defaultMessage="Link" />
              </Button>
              <Button variant="secondary">
                <FormattedMessage id="common.discard" defaultMessage="Discard" />
              </Button>
            </>
          }
        />

        <Row
          num="3"
          name="Destructive"
          count="4 uses"
          code='<Button variant="destructive">'
          purpose="Confirm side of a destructive action. ADR-0011: destructive never mutates on first click — always behind a second-step confirm."
          examples={[
            { file: "pages/settings/DeleteAccountCard.tsx:70", label: "Konto löschen — opens irreversible-deletion confirm" },
            { file: "pages/admin/QuestionnaireAdminPage.tsx:415", label: "Ja, löschen — admin questionnaire deletion" },
            { file: "pages/share/ShareItemDetailPage.tsx:289", label: "Ja, löschen — confirms removal of a shared item" },
          ]}
          specimens={
            <>
              <Button variant="destructive">
                <FormattedMessage id="common.delete" defaultMessage="Delete" />
              </Button>
              <Button variant="destructive">
                <FormattedMessage id="settings.styleguide.buttons.specimen.deleteAccount" defaultMessage="Delete account" />
              </Button>
            </>
          }
        />

        <Row
          num="4"
          name="Toggle"
          count="1 use"
          code='<Button variant="toggle" aria-pressed>'
          purpose="Sticky two-state pill where hover intentionally previews the NEXT click. For 'enter/leave mode' semantics with two equally-valid states. Click to feel the flip."
          examples={[
            { file: "pages/save/ProfilePage.tsx:620", label: "Sichtbarkeit bearbeiten / ausblenden — opens the per-entry visibility editor" },
          ]}
          notes="Down from 3 uses on this branch — Share-sidebar Beobachten + Interesse zeigen promoted to PRIMARY since their label change already carries the state signal."
          specimens={
            <>
              <Button
                variant="toggle"
                aria-pressed={chipState}
                onClick={() => setChipState((v) => !v)}
              >
                {chipState
                  ? intl.formatMessage({
                      id: "settings.styleguide.buttons.specimen.visibilityHide",
                      defaultMessage: "Hide visibility",
                    })
                  : intl.formatMessage({
                      id: "settings.styleguide.buttons.specimen.visibilityEdit",
                      defaultMessage: "Edit visibility",
                    })}
              </Button>
            </>
          }
        />

        <Row
          num="5"
          name="Chip"
          count="3 uses"
          code='<Button variant="chip" size="sm">'
          purpose="The third action tier between SECONDARY (commit + invert) and GHOST (no shape). For browse / explore clicks — 'open this picker', 'add another item', 'expand this section' — that shouldn't read as 'you're confirming something'."
          examples={[
            { file: "components/search/PersonFilterBuilder.tsx:178", label: "+ Filter hinzufügen — opens the picker for adding a person-search filter" },
            { file: "components/design/EntryFields.tsx:250", label: "+ Aufgabe — adds a new task line in job-activity editor" },
            { file: "components/design/EntryFields.tsx:258", label: "Aufgaben vorschlagen — AI-suggests activity bullets from job title" },
          ]}
          specimens={
            <>
              <Button variant="chip" size="sm">
                + <FormattedMessage id="settings.styleguide.buttons.specimen.addFilter" defaultMessage="Add filter" />
              </Button>
              <Button variant="chip" size="sm">
                <FormattedMessage id="settings.styleguide.buttons.specimen.adjustWeighting" defaultMessage="Adjust weighting" /> ↓
              </Button>
            </>
          }
        />

        <Row
          num="6"
          name="Ghost"
          count="3 uses"
          code='<Button variant="ghost">'
          purpose="The quietest action — pure text with a single hover-darken. Use when an action should be present but must not visually compete with surrounding content. Pattern across all 3 uses: opens a confirm dialog or toggles edit state."
          examples={[
            { file: "components/match/MatchWorkflow.tsx:127", label: "Match löschen — opens delete-confirm from the workflow toolbar" },
            { file: "components/admin/ReferenceDataList.tsx", label: "Bearbeiten — switches a reference-data row into edit mode" },
            { file: "components/admin/ReferenceDataList.tsx", label: "Löschen — opens delete-confirm for a reference-data row" },
          ]}
          specimens={
            <>
              <Button variant="ghost">
                <FormattedMessage id="common.edit" defaultMessage="Edit" />
              </Button>
              <Button variant="ghost">
                <FormattedMessage id="common.delete" defaultMessage="Delete" />
              </Button>
              <Button variant="ghost">
                <FormattedMessage id="settings.styleguide.buttons.specimen.deleteMatch" defaultMessage="Delete MATCH" />
              </Button>
            </>
          }
        />

        <Row
          num="7"
          name="Icon"
          count="8 uses"
          code='<Button variant="icon">'
          purpose="Icon-only button with a guaranteed square hit area + gentle chip-wash hover. Covers settings cogs, row Download / Open, and dismiss / delete close-X actions."
          examples={[
            { file: "components/match/MatchWorkflow.tsx:101", label: "Settings cog — opens MatchSettingsModal from the workflow topbar" },
            { file: "components/match/MatchModals.tsx", label: "Remove-person close-X in the access-list row" },
            { file: "components/match/ResultPanel.tsx", label: "Dismiss-Datenhinweis close-X in the result panel" },
            { file: "pages/save/service/DocItemRow.tsx:127/143", label: "Per-row Download + Open in the Save service list view" },
          ]}
          specimens={
            <>
              <Button variant="icon" size="sm" aria-label={intl.formatMessage({ id: "common.close", defaultMessage: "Close" })}>✕</Button>{/* i18n-allow */}
              <Button variant="icon" size="sm" aria-label={intl.formatMessage({ id: "settings.styleguide.buttons.specimen.download", defaultMessage: "Download" })}>↓</Button>{/* i18n-allow */}
              <Button variant="icon" size="sm" aria-label={intl.formatMessage({ id: "settings.styleguide.buttons.specimen.open", defaultMessage: "Open" })}>→</Button>{/* i18n-allow */}
            </>
          }
        />

        <Row
          num="8"
          name="Back"
          count="20 uses"
          code='<BackButton />'
          purpose="The universal '←' page-level nav-up affordance. Always calls navigate(-1), never a hard-coded route. Fixed shape, fixed size — typically in a mb-5 wrapper at the start of a page."
          examples={[
            { file: "pages/admin/QuestionnaireAdminPage.tsx:92", label: "Back from the admin questionnaire editor" },
            { file: "pages/grow/PowerDetailPage.tsx:72", label: "Back from an individual Power detail view" },
            { file: "pages/save/ProfilePage.tsx:566", label: "Back from a shared person's profile view" },
            { file: "pages/share/ShareCreatePage.tsx:327", label: "Back from Share item creation / preview" },
          ]}
          notes="Used across every pillar — Admin, Grow, Save, Share."
          specimens={<BackButton />}
        />
      </Section>

      <Section
        title={intl.formatMessage({
          id: "settings.styleguide.buttons.section2Title",
          defaultMessage: "2 – Sibling primitives",
        })}
        sub={intl.formatMessage({
          id: "settings.styleguide.buttons.section2Sub",
          defaultMessage:
            "Components in components/design/ (and components/common/ for BackButton) that aren't Buttons — distinct geometries / semantics.",
        })}
      >
        <Row
          num="9"
          name="Switch"
          count="8 uses"
          code='<Switch checked onChange [label={{on, off}}] />'
          purpose="Binary on/off. Bare without label; gains an inline labelled track when label={{on, off}} is provided. One primitive serving both modes."
          examples={[
            { file: "components/match/ChallengeStep.tsx:203", label: "Agentic-AI master switch in the Match team-builder" },
            { file: "components/match/requirements/row-primitives.tsx:262", label: "Per-row AI an / AI aus mode in the Match Requirements builder" },
            { file: "components/design/AddForm.tsx:111", label: "Teilen / Privat toggle in the add-entry form header" },
            { file: "pages/save/ProfilePage.tsx:641", label: "Bulk Share all / Keep private in the Profile visibility editor" },
          ]}
          specimens={
            <>
              <Switch checked={switchOn} onChange={setSwitchOn} ariaLabel="Bare switch demo" />{/* i18n-allow */}
              <Switch
                checked={switchOn2}
                onChange={setSwitchOn2}
                ariaLabel={
                  switchOn2
                    ? intl.formatMessage({ id: "settings.styleguide.buttons.specimen.share", defaultMessage: "Share" })
                    : intl.formatMessage({ id: "settings.styleguide.buttons.specimen.private", defaultMessage: "Private" })
                }
                label={{
                  on: intl.formatMessage({ id: "settings.styleguide.buttons.specimen.share", defaultMessage: "Share" }),
                  off: intl.formatMessage({ id: "settings.styleguide.buttons.specimen.private", defaultMessage: "Private" }),
                }}
              />
            </>
          }
        />

        <Row
          num="10"
          name="NumberStepper"
          count="1 use"
          code='<NumberStepper value onChange min max />'
          purpose="Increment / decrement a single numeric value within a tight inline range. ARIA spinbutton + keyboard (↑/↓/Home/End/Enter/Escape), type-to-edit, min/max clamping. Covered by NumberStepper.test.tsx."
          examples={[
            { file: "components/match/ChallengeStep.tsx:531", label: "Größe des Teams inline spinbutton in the Match Requirements panel" },
          ]}
          notes="Only one production callsite today but the primitive is fully covered for the next consumer."
          specimens={
            <NumberStepper
              value={stepperValue}
              onChange={setStepperValue}
              min={1}
              ariaLabel="Demo stepper" // i18n-allow
            />
          }
        />

        <Row
          num="11"
          name="PlusButton"
          count="3 uses"
          code='<PlusButton label onClick [glyph="+"] />'
          purpose="The slimmest '+ add' affordance — no ring, no fill, just uppercase tracking text. Composed inside <AddForm variant='row'> for Profile sections, or used directly for inline row-builders."
          examples={[
            { file: "components/match/requirements/FreitextSection.tsx:135", label: "+ Skill — appends a new expertise condition to Teamanforderungen" },
            { file: "components/match/requirements/FreitextSection.tsx:136", label: "+ Jobtitel — appends a new job-title condition" },
            { file: "components/design/AddForm.tsx:172", label: "Composed inside AddForm's 'row' variant (Profile 'add another' triggers)" },
          ]}
          specimens={
            <>
              <PlusButton
                label={intl.formatMessage({ id: "settings.styleguide.buttons.specimen.skill", defaultMessage: "Skill" })}
                onClick={() => {}}
              />
              <PlusButton
                label={intl.formatMessage({ id: "settings.styleguide.buttons.specimen.jobTitle", defaultMessage: "Job title" })}
                onClick={() => {}}
              />
              <PlusButton
                label={intl.formatMessage({ id: "settings.styleguide.buttons.specimen.disabled", defaultMessage: "Disabled" })}
                onClick={() => {}}
                disabled
              />
            </>
          }
        />

        <Row
          num="12"
          name="AddPlaceholder"
          count="1 use"
          code='<AddPlaceholder label onClick [fullWidth] />'
          purpose="Outlined-ring '+ add' affordance for empty states. Solid 15% ink ring — never dashed (ring-dashed isn't a real Tailwind class)."
          examples={[
            { file: "components/design/AddForm.tsx:176", label: "Composed inside AddForm's 'empty' variant — shown when a Profile section has zero entries" },
          ]}
          notes="Only directly imported by AddForm. All section-level placeholders reach this primitive via AddForm's conditional render."
          specimens={
            <>
              <AddPlaceholder
                label={intl.formatMessage({ id: "settings.styleguide.buttons.specimen.newFolder", defaultMessage: "New folder" })}
                onClick={() => {}}
              />
              <div className="w-80">
                <AddPlaceholder
                  label={intl.formatMessage({
                    id: "settings.styleguide.buttons.specimen.addWorkExperience",
                    defaultMessage: "Add work experience",
                  })}
                  onClick={() => {}}
                  fullWidth
                />
              </div>
            </>
          }
        />

        <Row
          num="13"
          name="AddForm"
          count="3 uses"
          code='<AddForm variant="empty"|"row" onAdd fields />'
          purpose="Behavioural composite — wraps <AddPlaceholder fullWidth> (variant='empty') or <PlusButton> (variant='row') and owns the click-to-expand-form state machine. Keeps the visual primitives free of behaviour."
          examples={[
            { file: "components/design/Section.tsx:49", label: "Empty-state trigger in the reusable Section wrapper" },
            { file: "components/design/Section.tsx:64", label: "Row-variant trigger appended below a non-empty section" },
            { file: "pages/save/ProfilePage.tsx:1001", label: "Berufserfahrung hinzufügen in the Profile career-graph footer" },
          ]}
          specimens={
            <div className="w-80">
              <AddForm
                variant="empty"
                label={intl.formatMessage({ id: "design.addEntry", defaultMessage: "Add entry" })}
                fields={[
                  {
                    key: "name",
                    label: intl.formatMessage({ id: "settings.styleguide.buttons.specimen.fieldName", defaultMessage: "Name" }),
                    type: "text",
                    required: true,
                  },
                  {
                    key: "note",
                    label: intl.formatMessage({ id: "settings.styleguide.buttons.specimen.fieldNote", defaultMessage: "Note" }),
                    type: "text",
                  },
                ]}
                onAdd={(payload) => console.log("[Styleguide] AddForm", payload)}
              />
            </div>
          }
        />

        <Row
          num="14"
          name="MenuItem"
          count="2 uses"
          code='<MenuItem icon label [active] onClick />'
          purpose="Full-width left-aligned row affordance for popovers / dropdowns / sidebars. One size only — no sm/md drift. Also exports menuItemClasses() for NavLink consumers."
          examples={[
            { file: "components/layout/SearchBar.tsx:491", label: "Pin / Unpin action in the topbar search-results popover" },
            { file: "pages/admin/QuestionnaireAdminPage.tsx:116", label: "Questionnaire selection in the admin sidebar list" },
          ]}
          specimens={
            <div className="w-60 rounded-md bg-page p-1 ring-1 ring-ink/10">
              <MenuItem icon={<span>⚙</span>}>
                <FormattedMessage id="settings.styleguide.buttons.specimen.menuSettings" defaultMessage="Settings" />
              </MenuItem>
              <MenuItem icon={<span>★</span>} active>
                <FormattedMessage id="settings.styleguide.buttons.specimen.menuCareerQuestionnaire" defaultMessage="Career questionnaire" />
              </MenuItem>
              <MenuItem icon={<span>↗</span>}>
                <FormattedMessage id="settings.styleguide.buttons.specimen.share" defaultMessage="Share" />
              </MenuItem>
            </div>
          }
        />

        <Row
          num="15"
          name="SegmentedControl"
          count="2 uses"
          code='<SegmentedControl value onChange options />'
          purpose="Pick exactly one of N options. There's no no-filter state — one option is always selected."
          examples={[
            { file: "pages/grow/PowerLibraryPage.tsx:138", label: "Score-source switch (Smart Analysis ↔ Selbstbewertung) across Power Library tiles" },
            { file: "pages/save/profile/sidebars.tsx:51", label: "Same Smart-Analysis / Selbstbewertung switch in the Profile Powers sidebar" },
          ]}
          specimens={
            <SegmentedControl
              value={seg}
              onChange={setSeg}
              options={[
                {
                  value: "smart",
                  label: intl.formatMessage({ id: "settings.styleguide.buttons.specimen.smartAnalysis", defaultMessage: "Smart Analysis" }),
                },
                {
                  value: "self",
                  label: intl.formatMessage({ id: "settings.styleguide.buttons.specimen.selfAssessment", defaultMessage: "Self-assessment" }),
                },
              ]}
            />
          }
        />

        <Row
          num="16"
          name="FilterChip"
          count="5 uses"
          code='<FilterChip active onClick>'
          purpose="Ambient list-page filter with an explicit 'Alle' no-filter default. Active state is full invert (same recipe as SECONDARY hover)."
          examples={[
            { file: "pages/grow/PowerLibraryPage.tsx:194", label: "Alle / Unbewertete — narrows the 30 powers to just the unrated ones" },
            { file: "pages/grow/GrowWatchPage.tsx:159", label: "Alle / Video / Podcast / Script — narrows the Grow Watch content feed" },
            { file: "pages/grow/GrowPlayPage.tsx:187", label: "Alle / Solo / Team — narrows the Grow Play scenarios by group size" },
            { file: "pages/share/ShareMinePage.tsx:77", label: "Alle / Projekte / Posts — narrows the user's own shared items" },
          ]}
          notes="All three Grow pages now use the canonical primitive — visually identical filter rows."
          specimens={
            <>
              <FilterChip active={filter === "alle"} onClick={() => setFilter("alle")}>
                <FormattedMessage id="settings.styleguide.buttons.specimen.filterAll" defaultMessage="ALL" />
              </FilterChip>
              <FilterChip active={filter === "projekte"} onClick={() => setFilter("projekte")}>
                <FormattedMessage id="settings.styleguide.buttons.specimen.filterProjects" defaultMessage="PROJECTS" />
              </FilterChip>
              <FilterChip active={filter === "posts"} onClick={() => setFilter("posts")}>
                <FormattedMessage id="settings.styleguide.buttons.specimen.filterPosts" defaultMessage="POSTS" />
              </FilterChip>
            </>
          }
        />

        <Row
          num="17"
          name="Disclosure"
          count="3 uses"
          code='<Disclosure title count [defaultOpen] />'
          purpose="Collapsible section — header row that toggles a divided body below it. Use whenever inline content needs a true header-and-body expand/collapse seam. State controlled or uncontrolled."
          examples={[
            { file: "components/match/ChallengeStep.tsx:554", label: "Team Powers — collapsible section in the Match challenge builder" },
            { file: "components/match/ResultStep.tsx:358", label: "Facts & Skills — collapsible in the Match Result Details box" },
            { file: "components/match/ResultStep.tsx:361", label: "Team Powers — sibling disclosure in the same Details box" },
          ]}
          notes="All 3 uses are in Match. Not (yet) used elsewhere."
          specimens={
            <div className="w-full max-w-md">
              <Disclosure
                title={intl.formatMessage({ id: "settings.styleguide.buttons.specimen.teamPowers", defaultMessage: "Team Powers" })}
                count={8}
                defaultOpen
              >
                <div className="text-[12px] text-ink/55">
                  <FormattedMessage
                    id="settings.styleguide.buttons.specimen.disclosureBody"
                    defaultMessage="Clicking the header rotates the chevron by 90° and opens the panel. A hairline separates header and body."
                  />
                </div>
              </Disclosure>
            </div>
          }
        />
      </Section>

      <Section
        title={intl.formatMessage({
          id: "settings.styleguide.buttons.section3Title",
          defaultMessage: "3 – Audit notes",
        })}
        sub={intl.formatMessage({
          id: "settings.styleguide.buttons.section3Sub",
          defaultMessage:
            "The button-alignment pass has no known leftover button-pattern drift in its audited scope. Broader overlay rows, cards, local pickers, colours, z-indexes, and typography remain future UI-system cleanup and are intentionally not claimed as solved here.",
        })}
        success
      />

      {/* Summary */}
      <div className="box bg-surface p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-ink/55">
          <FormattedMessage id="settings.styleguide.buttons.summaryTitle" defaultMessage="Summary" />
        </div>
        <div className="mt-3 space-y-3 text-[12px] leading-relaxed text-ink/70">
          <p>
            <span className="font-medium text-ink">
              <FormattedMessage id="settings.styleguide.buttons.summaryButtonsLabel" defaultMessage="Canonical '<Button>' variants:" />
            </span>{" "}
            <FormattedMessage
              id="settings.styleguide.buttons.summaryButtonsBody"
              defaultMessage="7 – primary (44), secondary (55), destructive (4), toggle (1), chip (4), ghost (3), icon (8). Plus '<BackButton>' (20)."
            />
          </p>
          <p>
            <span className="font-medium text-ink">
              <FormattedMessage id="settings.styleguide.buttons.summaryPrimitivesLabel" defaultMessage="Sibling primitives:" />
            </span>{" "}
            <FormattedMessage
              id="settings.styleguide.buttons.summaryPrimitivesBody"
              defaultMessage="9 – Switch (8), NumberStepper (1), PlusButton (3), AddPlaceholder (1), AddForm (3), MenuItem (2), SegmentedControl (2), FilterChip (5), Disclosure (3)."
            />
          </p>
          <p>
            <span className="font-medium text-emerald-700">
              <FormattedMessage id="settings.styleguide.buttons.summaryDrift" defaultMessage="Audited button-pattern drift: none known." />
            </span>
            <span>
              {" "}
              <FormattedMessage id="settings.styleguide.buttons.summaryDriftScope" defaultMessage="Other UI surfaces are outside this snapshot's scope." />
            </span>
          </p>
          <p className="text-[11px] italic text-ink/50">
            <FormattedMessage
              id="settings.styleguide.buttons.summaryFootnote"
              defaultMessage="Counts are production callsites only (exclude each primitive's own definition file and Styleguide specimens). Snapshot date: {date}."
              values={{ date: <span className="font-mono">{SNAPSHOT_DATE}</span> }}
            />
          </p>
        </div>
      </div>

      {/* Reading notes */}
      <div className="box bg-surface p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-ink/55">
          <FormattedMessage id="settings.styleguide.buttons.readingNotesTitle" defaultMessage="Reading notes" />
        </div>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-[12px] leading-relaxed text-ink/70">
          <li>
            <span className="font-medium text-ink">
              <FormattedMessage id="settings.styleguide.buttons.noteSecondaryToggleLabel" defaultMessage="SECONDARY vs. TOGGLE." />
            </span>{" "}
            <FormattedMessage
              id="settings.styleguide.buttons.noteSecondaryToggleBody"
              defaultMessage="Identical at rest. Hover behaviour distinguishes them – SECONDARY fully inverts on hover (commit affordance); TOGGLE inverts to preview the NEXT click (state preview)."
            />
          </li>
          <li>
            <span className="font-medium text-ink">
              <FormattedMessage id="settings.styleguide.buttons.noteSecondaryChipLabel" defaultMessage="SECONDARY vs. CHIP." />
            </span>{" "}
            <FormattedMessage
              id="settings.styleguide.buttons.noteSecondaryChipBody"
              defaultMessage="Identical geometry. CHIP's hover is a SOFT 6% wash; SECONDARY's is a FULL invert. Use CHIP for browse / explore (open picker, expand section); use SECONDARY for commit / cancel."
            />
          </li>
          <li>
            <span className="font-medium text-ink">
              <FormattedMessage id="settings.styleguide.buttons.noteLabelStateLabel" defaultMessage="When state communicates via label." />
            </span>{" "}
            <FormattedMessage
              id="settings.styleguide.buttons.noteLabelStateBody"
              defaultMessage={
                "ShareItemDetailPage's Watch + Express interest are PRIMARY (not TOGGLE) — the label changes (\"✓ Watching - remove\" / \"✓ Interested - withdraw\") carry the state signal. Use TOGGLE only when the visual flip is the affordance."
              }
            />
          </li>
        </ul>
      </div>
    </>
  );
}
